import React, { useState, useEffect } from 'react';
import api from '../api';
import { ChartLine, Users, FileText, ClockCounterClockwise, Warning, Wrench, CreditCard } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ClientManagement from '../components/ClientManagement';
import PrestadorManagement from '../components/PrestadorManagement';
import AuxiliarManagement from '../components/AuxiliarManagement';
import GerenciarSolicitacoes from '../components/GerenciarSolicitacoes';
import ReportsView from '../components/ReportsView';
import ServiceManagement from '../components/ServiceManagement';
import PlanManagement from '../components/PlanManagement';
import { useAuth } from '../AuthContext';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [lowHoursClients, setLowHoursClients] = useState([]);
  const [solicitacoesStats, setSolicitacoesStats] = useState({ pendentes: [], atribuidas: [] });
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, lowHoursRes, solRes, clientsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/notifications/low-hours'),
        api.get('/solicitacoes'),
        api.get('/clients')
      ]);
      setStats(statsRes.data);
      setLowHoursClients(lowHoursRes.data);
      
      const allSol = solRes.data;
      const allClients = clientsRes.data;
      const getClientName = (id) => allClients.find(c => c.id === id)?.name || 'N/A';
      
      const pendentes = allSol.filter(s => s.status === 'pendente').map(s => ({
        ...s, client_name: getClientName(s.client_id)
      }));
      const atribuidas = allSol.filter(s => s.status === 'atribuida' || s.status === 'em_andamento').map(s => ({
        ...s, client_name: getClientName(s.client_id)
      }));
      
      setSolicitacoesStats({ pendentes, atribuidas });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTempoEspera = (createdAt) => {
    const diffMs = new Date() - new Date(createdAt);
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h`;
    return `${Math.floor(diffMs / (1000 * 60))}min`;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Carregando...</div>;
  }

  const COLORS = ['#FFB800', '#E6A600', '#22C55E'];

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#050505] border-b border-[#27272A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="/toolserv-logo.png" 
            alt="Toolserv"
            className="h-8"
          />
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm">Administrador</span>
            <button 
              onClick={logout}
              className="px-4 py-2 bg-[#1E1E1E] text-white border border-[#27272A] rounded-sm hover:bg-[#27272A] transition-colors text-sm"
              data-testid="logout-button"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-[#121212] border-b border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: ChartLine },
              { id: 'solicitacoes', label: 'Solicitacoes', icon: FileText },
              { id: 'clients', label: 'Clientes', icon: Users },
              { id: 'prestadores', label: 'Prestadores', icon: Users },
              { id: 'auxiliares', label: 'Auxiliares', icon: Users },
              { id: 'services', label: 'Servicos', icon: Wrench },
              { id: 'planos', label: 'Planos', icon: CreditCard },
              { id: 'reports', label: 'Relatorios', icon: FileText }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id 
                    ? 'border-[#FFB800] text-[#FFB800]' 
                    : 'border-transparent text-zinc-400 hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon size={20} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'dashboard' && (
          <div>
            {/* Low Hours Alert */}
            {lowHoursClients.length > 0 && (
              <div className="mb-6 p-4 bg-gradient-to-r from-[#FFB800] to-[#E6A600] rounded-sm flex items-center gap-3" data-testid="low-hours-alert">
                <Warning size={24} weight="fill" className="text-black" />
                <div className="text-black font-semibold">
                  {lowHoursClients.length} cliente(s) com horas baixas!
                </div>
              </div>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-clients">
                <div className="text-zinc-400 text-sm mb-2">Total de Clientes</div>
                <div className="metric-value">{stats?.total_clients || 0}</div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-collaborators">
                <div className="text-zinc-400 text-sm mb-2">Colaboradores</div>
                <div className="metric-value">{stats?.total_collaborators || 0}</div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-reports">
                <div className="text-zinc-400 text-sm mb-2">Relatórios</div>
                <div className="metric-value">{stats?.total_reports || 0}</div>
              </div>
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6" data-testid="stat-available-hours">
                <div className="text-zinc-400 text-sm mb-2">Horas Disponíveis</div>
                <div className="metric-value">{stats?.total_available_hours?.toFixed(1) || 0}</div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
                <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  Horas Contratadas vs Usadas
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { name: 'Contratadas', value: stats?.total_contracted_hours || 0 },
                    { name: 'Usadas', value: stats?.total_used_hours || 0 },
                    { name: 'Disponíveis', value: stats?.total_available_hours || 0 }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis dataKey="name" stroke="#A1A1AA" />
                    <YAxis stroke="#A1A1AA" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A' }}
                      labelStyle={{ color: '#FFFFFF' }}
                    />
                    <Bar dataKey="value" fill="#FFB800" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-[#121212] border border-[#27272A] rounded-sm p-6">
                <h3 className="text-xl font-semibold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  Clientes com Horas Baixas
                </h3>
                {lowHoursClients.length > 0 ? (
                  <div className="space-y-3">
                    {lowHoursClients.map(client => (
                      <div key={client.client_id} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-medium">{client.client_name}</span>
                          <span className="text-[#FFB800] font-bold">{client.available_hours.toFixed(1)}h</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-zinc-400 text-center py-8">Nenhum cliente com horas baixas</div>
                )}
              </div>
            </div>

            {/* Solicitacoes Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Aguardando Tecnico (Pendentes) */}
              <div className="bg-[#121212] border border-red-900/30 rounded-sm p-6" data-testid="panel-aguardando-tecnico">
                <h3 className="text-xl font-semibold text-red-400 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  Aguardando Tecnico ({solicitacoesStats.pendentes.length})
                </h3>
                {solicitacoesStats.pendentes.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {solicitacoesStats.pendentes.map(sol => (
                      <div key={sol.id} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-[#FFB800] font-semibold text-sm">{sol.numero_solicitacao}</span>
                            <span className="text-white text-sm ml-2">{sol.client_name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-red-400 text-sm font-bold">{getTempoEspera(sol.created_at)}</div>
                            <div className="text-zinc-500 text-xs">esperando</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {sol.servicos_solicitados?.slice(0, 2).map((s, i) => (
                            <span key={i} className="text-xs bg-[#121212] px-2 py-0.5 rounded text-zinc-400">{s}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-zinc-400 text-center py-8">Nenhuma solicitacao pendente</div>
                )}
              </div>

              {/* Em Atendimento (Atribuidas) */}
              <div className="bg-[#121212] border border-blue-900/30 rounded-sm p-6" data-testid="panel-em-atendimento">
                <h3 className="text-xl font-semibold text-blue-400 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  Em Atendimento ({solicitacoesStats.atribuidas.length})
                </h3>
                {solicitacoesStats.atribuidas.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {solicitacoesStats.atribuidas.map(sol => (
                      <div key={sol.id} className="p-3 bg-[#050505] border border-[#27272A] rounded-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-[#FFB800] font-semibold text-sm">{sol.numero_solicitacao}</span>
                            <span className="text-white text-sm ml-2">{sol.client_name}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-blue-400 text-sm font-medium">{sol.prestador_nome || 'N/A'}</div>
                            <div className="text-zinc-500 text-xs">{getTempoEspera(sol.created_at)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-zinc-400 text-center py-8">Nenhum atendimento em andamento</div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'solicitacoes' && <GerenciarSolicitacoes />}
        {activeTab === 'clients' && <ClientManagement />}
        {activeTab === 'prestadores' && <PrestadorManagement />}
        {activeTab === 'auxiliares' && <AuxiliarManagement />}
        {activeTab === 'services' && <ServiceManagement />}
        {activeTab === 'planos' && <PlanManagement />}
        {activeTab === 'reports' && <ReportsView />}
      </div>
    </div>
  );
};

export default AdminDashboard;
